import * as React from 'react';
import * as $ from 'jquery';
import * as _ from 'underscore';

import { ParameterField } from '../../../../_common/platform/tsx/ParameterField';
import BooleanField from '../BooleanField';
import SelectField from '../SelectField';
import StringField from '../StringField';
import { DataEditMode } from './DataEditConfigurator';
import { IValuesCollection } from '../../ts/appTheme';
import { EditingMode, IDataEditConfiguratorValueOptions, IWidgetThemeFields } from './Interfaces';

export default function OptionsTab(props: {
    mode: DataEditMode;
    value: IDataEditConfiguratorValueOptions;
    theme: IValuesCollection;
    widgetThemeFields: IWidgetThemeFields;

    onChange(value: IDataEditConfiguratorValueOptions): void;
}) {
    const getValueCopy = () => {
        return $.extend(true, {}, props.value);
    };

    const processOnValueChange = (value: IDataEditConfiguratorValueOptions) => {
        const result = !_.isEmpty(value) ? value : null;

        props.onChange(result);
    };

    const inheritedAllowInserting = props.theme[props.widgetThemeFields.allowInserting] !== false;
    const inheritedAllowUpdating = props.theme[props.widgetThemeFields.allowUpdating] !== false;
    const inheritedAllowDeleting = props.theme[props.widgetThemeFields.allowDeleting] !== false;
    const inheritedMultiRowOperations = props.theme[props.widgetThemeFields.multiRowOperations] === true;
    const inheritedEditingMode: EditingMode = props.theme[props.widgetThemeFields.editingMode] || 'dialog';

    const handleOnAllowInsertingChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value !== inheritedAllowInserting) {
            valueCopy.allowInserting = value;
        } else {
            delete valueCopy.allowInserting;
        }

        processOnValueChange(valueCopy);
    };

    const handleOnAllowUpdatingChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value !== inheritedAllowUpdating) {
            valueCopy.allowUpdating = value;
        } else {
            delete valueCopy.allowUpdating;
        }

        processOnValueChange(valueCopy);
    };

    const handleOnAllowDeletingChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value !== inheritedAllowDeleting) {
            valueCopy.allowDeleting = value;
        } else {
            delete valueCopy.allowDeleting;
        }

        processOnValueChange(valueCopy);
    };

    const handleOnMulitiRowOperationsChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value !== inheritedMultiRowOperations) {
            valueCopy.multiRowOperations = value;
        } else {
            delete valueCopy.multiRowOperations;
        }

        processOnValueChange(valueCopy);
    };

    const handleOnEditingModeEditChange = (value: EditingMode) => {
        const valueCopy = getValueCopy();

        if (value !== 'dialog' && props.mode !== 'history') {
            delete valueCopy.dialogTitle;
        }

        if (value !== inheritedEditingMode) {
            valueCopy.editingMode = value;
        } else {
            delete valueCopy.editingMode;
        }

        processOnValueChange(valueCopy);
    };

    const handleOnDialogTitleChange = (value: string) => {
        const valueCopy = getValueCopy();

        if (value !== '') {
            valueCopy.dialogTitle = value;
        } else {
            delete valueCopy.dialogTitle;
        }

        processOnValueChange(valueCopy);
    };

    const value = props.value || {};
    const editingMode = value.editingMode || inheritedEditingMode;

    let elements: React.JSX.Element;

    if (props.mode === 'grid') {
        elements = (
            <>
                <ParameterField noTitle={true} clearable={false} showInheritedIcon={value.allowInserting == null}>
                    <BooleanField
                        value={value.allowInserting != null ? value.allowInserting : inheritedAllowInserting}
                        title={'Allow Inserting'}
                        allowNull={false}
                        onChange={handleOnAllowInsertingChange}
                    />
                </ParameterField>

                <ParameterField noTitle={true} clearable={false} showInheritedIcon={value.allowUpdating == null}>
                    <BooleanField
                        value={value.allowUpdating != null ? value.allowUpdating : inheritedAllowUpdating}
                        title={'Allow Updating'}
                        allowNull={false}
                        onChange={handleOnAllowUpdatingChange}
                    />
                </ParameterField>

                <ParameterField noTitle={true} clearable={false} showInheritedIcon={value.allowDeleting == null}>
                    <BooleanField
                        value={value.allowDeleting != null ? value.allowDeleting : inheritedAllowDeleting}
                        title={'Allow Deleting'}
                        allowNull={false}
                        onChange={handleOnAllowDeletingChange}
                    />
                </ParameterField>

                <ParameterField noTitle={true} clearable={false} showInheritedIcon={value.multiRowOperations == null}>
                    <BooleanField
                        value={
                            value.multiRowOperations != null ? value.multiRowOperations : inheritedMultiRowOperations
                        }
                        title={'Multi Row Operations'}
                        allowNull={false}
                        onChange={handleOnMulitiRowOperationsChange}
                    />
                </ParameterField>

                <ParameterField title={'Editing Mode'} clearable={false} showInheritedIcon={value.editingMode == null}>
                    <SelectField
                        value={editingMode}
                        allowNull={false}
                        options={getEditingModeSelectorOptions()}
                        onChange={handleOnEditingModeEditChange}
                    />
                </ParameterField>

                {editingMode === 'dialog' ? (
                    <DialogTitleField value={value} onChange={handleOnDialogTitleChange} />
                ) : null}
            </>
        );
    } else if (props.mode === 'history') {
        elements = <DialogTitleField value={value} onChange={handleOnDialogTitleChange} />;
    }

    return <div className="options-tab">{elements}</div>;
}

function DialogTitleField(props: { value: IDataEditConfiguratorValueOptions; onChange(value: string): void }) {
    return (
        <ParameterField title={'Dialog Title'} clearable={false}>
            <StringField value={props.value.dialogTitle || ''} allowNull={false} onChange={props.onChange} />
        </ParameterField>
    );
}

export function getEditingModeSelectorOptions() {
    return {
        values: ['dialog', 'cell', 'row', 'form'],
        display: ['Popup Dialog', 'One Cell At Once', 'One Row At Once', 'Inline Form']
    };
}
